"use client";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NativeInput, RoundedButton } from "@tosize/ui";
import { filter } from "fuzzy";
import { DropdownMenu } from "radix-ui";
import { type ChangeEvent, useDeferredValue, useMemo, useState } from "react";
import { FilterItem } from "~/app/[lang]/(default)/overview/[type]/[id]/DIYListing/FilterItem";
import { useDIYFilter } from "~/app/[lang]/(default)/overview/[type]/[id]/DIYListing/useDIYListing";
import type { DIYAggregation } from "~/graphql/shared/page";

interface FilterButtonProps {
	aggregation: DIYAggregation;
	searchMessage: string;
	emptyMessage: string;
}

export function FilterButton({ aggregation, searchMessage, emptyMessage }: FilterButtonProps) {
	const { activeOptions } = useDIYFilter({ aggregation });

	const [search, setSearch] = useState("");
	const query = useDeferredValue(search);

	function onSearchChange(evt: ChangeEvent<HTMLInputElement>) {
		setSearch(evt.target.value);
	}

	const options = useMemo(() => {
		const filtered = filter(query, aggregation.options, {
			pre: "<b>",
			post: "</b>",
			extract: (option) => option.label ?? "",
		});

		return filtered.map((result) => ({
			...(aggregation.options[result.index] as DIYAggregation["options"][number]),
			label: result.string,
		}));
	}, [query, aggregation.options]);

	if (aggregation.options.length === 0) {
		return null;
	}
	return (
		<>
			<DropdownMenu.Root>
				<DropdownMenu.Trigger asChild>
					<RoundedButton className="hidden xl:flex">
						{aggregation.label}

						{activeOptions.length > 0 && (
							<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-dark text-white text-xs">
								{activeOptions.length}
							</span>
						)}

						<FontAwesomeIcon icon={faChevronDown} className="ml-auto h-4 w-4" />
					</RoundedButton>
				</DropdownMenu.Trigger>

				<DropdownMenu.Portal>
					<DropdownMenu.Content className="flex max-h-96 min-w-[300px] flex-col gap-4 overflow-y-scroll rounded bg-white p-4 shadow">
						{aggregation.options.length > 5 && (
							<NativeInput
								placeholder={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
								aria-label={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
								type="text"
								value={search}
								onChange={onSearchChange}
								onKeyDown={(e) => e.stopPropagation()}
							/>
						)}

						{options.length === 0 ? (
							<DropdownMenu.Item>{emptyMessage}</DropdownMenu.Item>
						) : (
							options.map((option) => (
								<DropdownMenu.Item key={option.value} asChild>
									<FilterItem aggregation={aggregation} option={option} />
								</DropdownMenu.Item>
							))
						)}
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
		</>
	);
}
